import * as Sentry from "@sentry/react";
import ReactDOM from "react-dom/client";
import Ranking from "./components/ranking";
import { findTargets } from "./embed";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "production",
  integrations: [
    Sentry.thirdPartyErrorFilterIntegration({
      // This key needs to exactly match the configuration in vite.config.js
      filterKeys: [import.meta.env.VITE_SENTRY_APPLICATION_KEY],
      behaviour: "drop-error-if-contains-third-party-frames",
    }),
  ],
});
Sentry.setTag("entry", "ranking");

const FATHOM_SRC = "https://cdn-eu.usefathom.com/script.js";

function loadFathom() {
  const site = import.meta.env.VITE_FATHOM_ID;

  if (!site || document.querySelector(`script[src="${FATHOM_SRC}"]`)) return;

  const script = document.createElement("script");
  script.src = FATHOM_SRC;
  script.dataset.site = site;
  script.dataset.spa = "auto";
  script.defer = true;

  document.head.append(script);
}

// Keyed by the element object, so a page rebuilt from a snapshot renders fresh.
// Weak, so the root of a removed container can be collected.
const roots = new WeakMap();

/**
 * The element nodes among a mutation's added or removed nodes. Text and comment
 * nodes cannot hold a container.
 *
 * @param {NodeList} nodes
 * @returns {Element[]}
 */
const elementsIn = (nodes) =>
  [...nodes].filter((node) => node.nodeType === Node.ELEMENT_NODE);

/**
 * @param {Element} target
 */
function mount(target) {
  if (roots.has(target)) return;

  const root = ReactDOM.createRoot(target);
  roots.set(target, root);
  root.render(<Ranking source={target.dataset.fathomSource} />);
}

/**
 * @param {Element} target
 */
function unmount(target) {
  roots.get(target)?.unmount();
  roots.delete(target);
}

loadFathom();

// This script will loaded as a module, so the containers should be there.
findTargets(document.documentElement).forEach(mount);

// Mount new ones when they appear
// Unmount on removal
new MutationObserver((mutations) => {
  mutations.forEach(({ addedNodes, removedNodes }) => {
    elementsIn(removedNodes).forEach((node) =>
      findTargets(node).forEach(unmount),
    );
    elementsIn(addedNodes).forEach((node) => findTargets(node).forEach(mount));
  });
}).observe(document.documentElement, { childList: true, subtree: true });
